import './styles.scss'
import { useRef, useState, useMemo, useEffect } from 'react'
import classNames from 'classnames'
import { group, max, maxIndex } from 'd3-array'
import { scaleLinear } from 'd3-scale'
import groveDotImage from '../images/groveDot.png'

export default function GroveGraph(props) {
  let { yearExtent, year, groves, width, copyBlocks, responsive = false } = props

  const grovesByYear = useMemo(() => {
    if (!groves || !groves.groves) return []
    const filteredGroves = groves.groves.filter(g => g.yearDedicated >= yearExtent[0] && g.yearDedicated <= yearExtent[1])
    const grovesByYear = Array.from(group(filteredGroves, d => d.yearDedicated)).map(d => ({
      year: +d[0],
      groves: d[1],
      count: d[1].length,
    }))
    grovesByYear.sort((a, b) => a.year - b.year)
    return grovesByYear
  }, [groves, yearExtent])

  const maxGrovesByYear = useMemo(() => max(grovesByYear, d => d.count), [grovesByYear])

  const svgWidth = width
  const margins = {
    top: 20, left: 20, right: 30, bottom: 30,
  }
  width -= margins.left + margins.right
  const yearDelta = yearExtent[1] - yearExtent[0]
  const barWidth = Math.floor(Math.min(17, width / yearDelta))

  let height = maxGrovesByYear * barWidth
  margins.top = Math.floor(margins.top / barWidth) * barWidth
  height = Math.floor(height / barWidth) * barWidth
  margins.bottom = Math.floor(margins.bottom / barWidth) * barWidth

  const svgHeight = height + margins.top + margins.bottom
  const xScale = scaleLinear()
    .domain(yearExtent)
    .range([0, width])

  const yScale = scaleLinear()
    .domain([0, maxGrovesByYear])
    .range([ 0, barWidth * maxGrovesByYear])


  const maxYearValue = useRef(0)
  const currentYearValue = useRef(0)
  const yearBars = grovesByYear.map(yearDatum => {
    const x = xScale(yearDatum.year) - barWidth / 2
    const barHeight = barWidth * yearDatum.count
    if (yearDatum.year <= year) {
      currentYearValue.current = yearDatum.count
      maxYearValue.current = Math.max(maxYearValue.current, yearDatum.count)
    }
    const y = barWidth * maxGrovesByYear - barHeight
    return (
      <g transform={`translate(${x}, ${barWidth * 0})`} key={yearDatum.year}>
        <rect y={y} height={barHeight} width={barWidth} fill="url(#groveDotPattern)" />
      </g>
    )
  })


  const yearClipX = xScale(year)

  const xTicks = xScale.ticks(20)
  xTicks.unshift(yearExtent[0])
  const xTickLabels = xTicks.map((tick, tickIndex) => {
    const x = xScale(tick)
    const modulo = width < 380 ? 20 : 10
    const showLabel = tick === yearExtent[0] || tick === yearExtent[1] || ((tick % modulo === 0 && tick < yearExtent[1] - modulo) && tickIndex !== 1)
    return (
      <g transform={`translate(${x}, ${height})`} key={tick}>
        <circle r={2} fill="#14816e" />
        {
          showLabel ?
            <text opacity='0.7'  fontWeight='300' x={0} dy={'1em'} textAnchor="middle" fill="#14816e">{tick}</text>
          : null
        }
      </g>
    )
  })

  const yTicks = yScale.ticks(4)
  const yTickLabels = yTicks.map((tick, i) => {
    const y = height - tick * barWidth
    const showLabel = tick !== 0
    const current = false && tick === currentYearValue.current
    const stroke = current ? '#FE9362' : '#14816e'
    const strokeDashArray = current ? '4, 4' : null
    if (!current && maxYearValue.current < tick) {
      return null
    }
    return (
      <g transform={`translate(0, ${y})`} key={i}>
        <line x1={0} x2={width} stroke={stroke} opacity='0.7' strokeWidth={0.25} strokeDasharray={strokeDashArray} />
        {showLabel ?
          <text opacity='0.7' fontWeight='300' x={width} dx={'0.2em'} dy={'0.3em'} fill={stroke}>{tick}</text>
        : null}
      </g>
    )
  })


  const [graphInteractable, setGraphInteractable] = useState(false)
  const [hideLastTooltip, setHideLastTooltip] = useState(false)
  const [hoveredLineIndex, setHoveredLineIndex] = useState(null)
  const [tooltipOpacity, setTooltipOpacity] = useState(0)
  let copyBlockTooltipIndex = hoveredLineIndex !== null ? hoveredLineIndex : hideLastTooltip ? null : maxIndex(copyBlocks, d => year < d.year ? 0 : d.year)

  const hoverLine = (index) => () => {
    if (graphInteractable) {
      if (index === null) {
        setTooltipOpacity(0)
      } else {
        setHoveredLineIndex(index)
      }
    }
  }

  useEffect(() => {
    let timeoutId = null
    if (copyBlockTooltipIndex !== null) {
      timeoutId = setTimeout(() => {
        setTooltipOpacity(1)
      }, 300)
    }
    return () => {
      clearTimeout(timeoutId)
    }
  }, [copyBlockTooltipIndex])

  useEffect(() => {
    let timeoutId = null
    if (tooltipOpacity === 0 && hoveredLineIndex ) {
      timeoutId = setTimeout(() => {
        setHoveredLineIndex(null)
      }, 350)
    }

    return () => {
      clearTimeout(timeoutId)
    }
  }, [tooltipOpacity, hoveredLineIndex])

  const copyBlockLines = copyBlocks.map((copyBlock, index) => {
    const x = xScale(copyBlock.year)
    const visible = copyBlock.year <= year
    const style = {
      transition: '0.3s ease-in-out opacity',
      opacity: visible ? 1 : 0,
    }
    return (
      <g transform={`translate(${x}, 0)`} style={style} key={copyBlock.year} onMouseOver={hoverLine(index)} onMouseOut={hoverLine(null)}>
        <rect x={-10} width={20} height={barWidth * maxGrovesByYear} fill="transparent" />
        <line y1={0} y2={barWidth * maxGrovesByYear} stroke="#69A188" opacity='1' strokeDasharray='2,2' />
        <circle r={4} fill="#69A188" />
      </g>
    )
  })
  let copyBlockTooltip = null
  if (copyBlockTooltipIndex !== null) {
    const copyBlock = copyBlocks[copyBlockTooltipIndex]
    const x = xScale(copyBlock.year) + margins.left
    const flipX = x > svgWidth / 2
    const xPercent = (x / svgWidth) * 100
    const style = {
      left: `${xPercent}%`,
      transform: flipX ? 'translateX(calc(-100% - 10px))' : 'translateX(10px)',
      opacity: tooltipOpacity,
    }
    copyBlockTooltip = (
      <div className='copyBlockTooltip' style={style}>
        {copyBlock.text}
      </div>
    )
  }
  const done = year === yearExtent[1]


  useEffect(() => {
    let timeout = null
    if (done) {
      setGraphInteractable(true)
      timeout = setTimeout(() => {
        setTooltipOpacity(0)
        timeout = setTimeout(() => {
          setHideLastTooltip(true)
        }, 350)
      }, 6000)
    } else {
      setGraphInteractable(false)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [done])

  return (
    <div
      className={ classNames('GroveGraphContainer', { done, responsive })}
      style={responsive ? { aspectRatio: `${svgWidth} / ${svgHeight}` } : undefined}
    >
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        width={responsive ? '100%' : svgWidth}
        height={responsive ? '100%' : svgHeight}
        preserveAspectRatio="xMidYMid meet"
        className='GroveGraph'
        fontSize='0.875em'
        fontFamily='Roboto Condensed, sans-serif'
      >
        <defs>
          <pattern  width={barWidth} height={barWidth} id='groveDotPattern' patternUnits='userSpaceOnUse' >
            <image href={groveDotImage} width={barWidth} height={barWidth} />
          </pattern>
          <clipPath id='groveClipPath'>
            <rect width={yearClipX + margins.left} x={-margins.left} height={height} />
          </clipPath>

          <clipPath id='groveClipPathMargin'>
            <rect width={yearClipX + margins.left} x={-margins.left} height={height + margins.bottom + margins.top} y={-margins.top} />
          </clipPath>
          <clipPath id='groveClipPathBothMargin'>
            <rect width={yearClipX + margins.left + margins.right} x={-margins.left} height={height + margins.bottom + margins.top} y={-margins.top} />
          </clipPath>
        </defs>
        <g transform={`translate(${margins.left}, ${margins.top})`}>
          <g clipPath='url(#groveClipPathMargin)'>
            {xTickLabels}
          </g>
          <g>
            {yTickLabels}
          </g>
          <g clipPath={done ? null : 'url(#groveClipPath)'}>
            {yearBars}
          </g>
          <g clipPath='url(#groveClipPathBothMargin)'>
            {copyBlockLines}
          </g>
          <g transform={`translate(0, ${height})`}>
            <line stroke='#14816E' opacity='0.2' x2={width} />
            <line stroke='#14816E' x2={yearClipX} />
            <text x={yearClipX} dy='1em' textAnchor='middle' fill='#14816e' fontWeight='bold'>{Math.round(year)}</text>
          </g>
        </g>
      </svg>
      {copyBlockTooltip}
    </div>
  )
}
